import { useRef, useCallback, useState } from 'react';

interface Props {
  isHost: boolean;
  canControl: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  title: string;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (timeMs: number) => void;
  onStop: () => void;
  onToggleQueue: () => void;
  hasQueue: boolean;
  formatTime: (ms: number) => string;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onPiP?: () => void;
  isPiP?: boolean;
  onTuneOut?: () => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
  onTransferHost?: () => void;
  onSkip?: () => void;
}

export default function WatchPartyControls({
  isHost, canControl, isPlaying, currentTime, duration, title,
  onPlay, onPause, onSeek, onStop, onToggleQueue, hasQueue, formatTime,
  volume, onVolumeChange, onPiP, isPiP, onTuneOut, onFullscreen, isFullscreen, onTransferHost, onSkip,
}: Props) {
  const seekBarRef = useRef<HTMLDivElement>(null);
  const [showVolume, setShowVolume] = useState(false);
  const [premuteVolume, setPremuteVolume] = useState(1);

  const handleSeekClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!canControl || !seekBarRef.current || !duration) return;
    const rect = seekBarRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(pct * duration);
  }, [canControl, duration, onSeek]);

  const handleMuteToggle = useCallback(() => {
    if (volume > 0) {
      setPremuteVolume(volume);
      onVolumeChange(0);
    } else {
      onVolumeChange(premuteVolume || 1);
    }
  }, [volume, premuteVolume, onVolumeChange]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const volumeIcon = volume === 0 ? (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18c-.49.37-1.02.68-1.6.91-.36.15-.58.53-.58.92 0 .72.73 1.18 1.39.91.8-.33 1.55-.77 2.22-1.31l1.34 1.34a.996.996 0 101.41-1.41L5.05 3.63c-.39-.39-1.02-.39-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0014 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z"/>
    </svg>
  ) : volume < 0.5 ? (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M18.5 12A4.5 4.5 0 0016 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>
  );

  return (
    <div className="wp-controls">
      <div className="wp-seek-bar" ref={seekBarRef} onClick={handleSeekClick} style={{ cursor: canControl ? 'pointer' : 'default' }}>
        <div className="wp-seek-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="wp-controls-row">
        <div className="wp-controls-left">
          {canControl ? (
            <button className="wp-ctrl-btn" onClick={isPlaying ? onPause : onPlay} title={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? '⏸' : '▶'}
            </button>
          ) : (
            <span className="wp-sync-badge">SYNCED</span>
          )}
          {canControl && hasQueue && onSkip && (
            <button className="wp-ctrl-btn" onClick={onSkip} title="Skip to next">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z"/>
              </svg>
            </button>
          )}
          <span className="wp-time">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <div className="wp-volume" onMouseEnter={() => setShowVolume(true)} onMouseLeave={() => setShowVolume(false)}>
            <button className="wp-ctrl-btn wp-volume-btn" onClick={handleMuteToggle} title={volume === 0 ? 'Unmute' : 'Mute'}>
              {volumeIcon}
            </button>
            {showVolume && (
              <input
                type="range"
                className="wp-volume-slider"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              />
            )}
          </div>
        </div>
        <div className="wp-controls-center">
          <span className="wp-title" title={title}>{title}</span>
        </div>
        <div className="wp-controls-right">
          {onPiP && (
            <button className={`wp-ctrl-btn wp-pip-btn${isPiP ? ' active' : ''}`} onClick={onPiP} title={isPiP ? 'Exit Picture-in-Picture' : 'Picture-in-Picture'}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M19 11h-8v6h8v-6zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zm-2 .02H3V4.97h18v14.05z"/>
              </svg>
            </button>
          )}
          {onFullscreen && (
            <button className={`wp-ctrl-btn wp-fullscreen-btn${isFullscreen ? ' active' : ''}`} onClick={onFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              {isFullscreen ? (
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
              )}
            </button>
          )}
          {onTuneOut && (
            <button className="wp-ctrl-btn wp-tune-out-btn" onClick={onTuneOut} title="Tune Out">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
              </svg>
            </button>
          )}
          <button className="wp-ctrl-btn" onClick={onToggleQueue} title="Queue">
            📋{hasQueue ? ' •' : ''}
          </button>
          {isHost && onTransferHost && (
            <button className="wp-ctrl-btn" onClick={onTransferHost} title="Transfer Host">
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z"/>
              </svg>
            </button>
          )}
          {canControl && (
            <button className="wp-ctrl-btn wp-stop-btn" onClick={onStop} title="Stop Watch Party">
              ⏹
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
